import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { eventBus, MealEvents } from '@/lib/eventBus';
import { enqueueEffect, getSyncQueue, removeEffect, recordEffectFailure } from '@/lib/syncQueue';
import { toast } from '@/lib/toast';
import {
  mapRecipeToDb,
  mapRecipeIngredientToDb,
  mapMealPlanToDb,
  mapMealPlanIngredientToDb,
} from '@/logic/mealMapper';
import type { MealPlan, MealPlanIngredient, Recipe } from '@/types/meal';

/**
 * Offline-first worker for the Meal module. Mirrors StockEventHandler:
 * - subscribes to MealEvents.* and enqueues into the shared syncQueue
 * - on mount and on `online`, processes meal-prefixed effects
 * - drops effects older than 7 days (matches stock policy)
 * - removes effects on success AND failure to avoid blocking the queue
 *
 * Cooking a meal ALSO re-emits StockEvents.RECORD_USAGE per linked ingredient
 * so pantry decrements go through the battle-tested Stock code path.
 */
export function MealEventHandler(): null {
  const isProcessingRef = useRef(false);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current || !navigator.onLine) return;

    const queue = getSyncQueue();
    const mealEvents = queue.filter((e) => e.type.startsWith('MEAL_'));
    if (!mealEvents.length) return;

    isProcessingRef.current = true;

    for (const effect of mealEvents) {
      try {
        const ageMs = Date.now() - new Date(effect.timestamp).getTime();
        if (ageMs > 7 * 24 * 60 * 60 * 1000) {
          console.warn('MealEventHandler: dropping stale (>7d) effect', effect.id, effect.type);
          removeEffect(effect.id);
          continue;
        }

        switch (effect.type) {
          case MealEvents.RECIPE_CREATE:
            await handleRecipeCreate(effect.payload);
            break;
          case MealEvents.RECIPE_UPDATE:
            await handleRecipeUpdate(effect.payload);
            break;
          case MealEvents.RECIPE_DELETE:
            await handleRecipeDelete(effect.payload);
            break;
          case MealEvents.PLAN_CREATE:
            await handlePlanCreate(effect.payload);
            break;
          case MealEvents.PLAN_UPDATE:
            await handlePlanUpdate(effect.payload);
            break;
          case MealEvents.PLAN_DELETE:
            await handlePlanDelete(effect.payload);
            break;
          case MealEvents.PLAN_COOK:
            await handlePlanCook(effect.payload);
            break;
          case MealEvents.ATTENDEE_SET:
            await handleAttendeeSet(effect.payload);
            break;
          case MealEvents.ATTENDEE_REMOVE:
            await handleAttendeeRemove(effect.payload);
            break;
        }
        removeEffect(effect.id);
      } catch (err) {
        console.error('MealEventHandler failed', effect, err);
        // Bounded retry; after SYNC_MAX_ATTEMPTS the effect is parked in the
        // dead-letter queue (see FailedSyncRecovery in Settings) so a single
        // transient failure no longer destroys the user's mutation.
        recordEffectFailure(effect.id, err);
      }
    }

    isProcessingRef.current = false;
  }, []);

  useEffect(() => {
    const handlers: Array<[string, (p: any) => void]> = [
      [MealEvents.RECIPE_CREATE, (p) => enqueueAndKick(p, MealEvents.RECIPE_CREATE, processQueue, `${p?.recipe?.name ?? 'Recept'} toegevoegd`)],
      [MealEvents.RECIPE_UPDATE, (p) => enqueueAndKick(p, MealEvents.RECIPE_UPDATE, processQueue)],
      [MealEvents.RECIPE_DELETE, (p) => enqueueAndKick(p, MealEvents.RECIPE_DELETE, processQueue)],
      [MealEvents.PLAN_CREATE,   (p) => enqueueAndKick(p, MealEvents.PLAN_CREATE,   processQueue, `Maaltijd gepland`)],
      [MealEvents.PLAN_UPDATE,   (p) => enqueueAndKick(p, MealEvents.PLAN_UPDATE,   processQueue)],
      [MealEvents.PLAN_DELETE,   (p) => enqueueAndKick(p, MealEvents.PLAN_DELETE,   processQueue)],
      [MealEvents.PLAN_COOK,     (p) => enqueueAndKick(p, MealEvents.PLAN_COOK,     processQueue, `Maaltijd gekookt — voorraad bijgewerkt`)],
      [MealEvents.ATTENDEE_SET,    (p) => enqueueAndKick(p, MealEvents.ATTENDEE_SET,    processQueue)],
      [MealEvents.ATTENDEE_REMOVE, (p) => enqueueAndKick(p, MealEvents.ATTENDEE_REMOVE, processQueue)],
    ];
    for (const [evt, h] of handlers) eventBus.on(evt, h);
    window.addEventListener('online', processQueue);
    processQueue();
    return () => {
      for (const [evt, h] of handlers) eventBus.off(evt, h);
      window.removeEventListener('online', processQueue);
    };
  }, [processQueue]);

  return null;
}

function enqueueAndKick(payload: any, type: string, processQueue: () => void, successToast?: string) {
  enqueueEffect(type, payload);
  if (navigator.onLine) {
    processQueue();
    if (successToast) toast.success(successToast, { channel: 'stock' });
  } else if (successToast) {
    toast.info(`Offline: ${successToast} (in wachtrij)`, { channel: 'stock' });
  }
}

// ─── Handlers ─────────────────────────────────────────────────────────────

async function handleRecipeCreate(payload: { tempId: string; recipe: Recipe; teamId: string }) {
  const { recipe, teamId } = payload;
  const row = {
    team_id: teamId,
    ...mapRecipeToDb({
      name: recipe.name,
      notes: recipe.notes ?? null,
      imageUrl: recipe.imageUrl ?? null,
      servings: recipe.servings,
      prepMinutes: recipe.prepMinutes ?? null,
      cookMinutes: recipe.cookMinutes ?? null,
      effort: recipe.effort ?? null,
      tagIds: recipe.tagIds,
      metadata: recipe.metadata,
      source: recipe.source ?? null,
      createdByMemberId: recipe.createdByMemberId ?? null,
    }),
  };
  const { data, error } = await supabase.from('recipes').insert(row).select().single();
  if (error) throw error;
  const newId: string = data.id;
  if (recipe.ingredients?.length) {
    const ingRows = recipe.ingredients.map((ing, idx) => ({
      recipe_id: newId,
      ...mapRecipeIngredientToDb({ ...ing, recipeId: newId, sortOrder: ing.sortOrder ?? idx }),
    }));
    const { error: ingErr } = await supabase.from('recipe_ingredients').insert(ingRows);
    if (ingErr) throw ingErr;
  }
}

async function handleRecipeUpdate(payload: { id: string; updates: Partial<Recipe> }) {
  const { id, updates } = payload;
  const db = mapRecipeToDb(updates);
  if (Object.keys(db).length) {
    const { error } = await supabase.from('recipes').update(db).eq('id', id);
    if (error) throw error;
  }
  if (updates.ingredients) {
    // Simple strategy: delete & re-insert ingredient rows for the recipe.
    const { error: delErr } = await supabase.from('recipe_ingredients').delete().eq('recipe_id', id);
    if (delErr) throw delErr;
    if (updates.ingredients.length) {
      const rows = updates.ingredients.map((ing, idx) => ({
        recipe_id: id,
        ...mapRecipeIngredientToDb({ ...ing, recipeId: id, sortOrder: ing.sortOrder ?? idx }),
      }));
      const { error: insErr } = await supabase.from('recipe_ingredients').insert(rows);
      if (insErr) throw insErr;
    }
  }
}

async function handleRecipeDelete(payload: { id: string }) {
  const { error } = await supabase
    .from('recipes')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', payload.id);
  if (error) throw error;
}

async function handlePlanCreate(payload: { tempId: string; plan: MealPlan; teamId: string }) {
  const { plan, teamId } = payload;
  const row = {
    team_id: teamId,
    ...mapMealPlanToDb({
      plannedDate: plan.plannedDate,
      recipeId: plan.recipeId ?? null,
      name: plan.name,
      servings: plan.servings,
      notes: plan.notes ?? null,
      status: plan.status,
      createdByMemberId: plan.createdByMemberId ?? null,
    }),
  };
  const { data, error } = await supabase.from('meal_plans').insert(row).select().single();
  if (error) throw error;
  const newId: string = data.id;
  if (plan.ingredients?.length) {
    const ingRows = plan.ingredients.map((ing, idx) => ({
      meal_plan_id: newId,
      ...mapMealPlanIngredientToDb({ ...ing, mealPlanId: newId, sortOrder: ing.sortOrder ?? idx }),
    }));
    const { error: ingErr } = await supabase.from('meal_plan_ingredients').insert(ingRows);
    if (ingErr) throw ingErr;
  }
}

async function handlePlanUpdate(payload: { id: string; updates: Partial<MealPlan> }) {
  const { id, updates } = payload;
  const db = mapMealPlanToDb(updates);
  if (Object.keys(db).length) {
    const { error } = await supabase.from('meal_plans').update(db).eq('id', id);
    if (error) throw error;
  }
  if (updates.ingredients) {
    const { error: delErr } = await supabase.from('meal_plan_ingredients').delete().eq('meal_plan_id', id);
    if (delErr) throw delErr;
    if (updates.ingredients.length) {
      const rows = updates.ingredients.map((ing, idx) => ({
        meal_plan_id: id,
        ...mapMealPlanIngredientToDb({ ...ing, mealPlanId: id, sortOrder: ing.sortOrder ?? idx }),
      }));
      const { error: insErr } = await supabase.from('meal_plan_ingredients').insert(rows);
      if (insErr) throw insErr;
    }
  }
}

async function handlePlanDelete(payload: { id: string }) {
  const { error } = await supabase
    .from('meal_plans')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', payload.id);
  if (error) throw error;
}

async function handlePlanCook(payload: {
  id: string;
  plan: MealPlan;
  memberId?: string;
  teamId: string;
}) {
  const { id, plan, memberId, teamId } = payload;
  const cookedAt = plan.cookedAt || new Date();

  const { error } = await supabase
    .from('meal_plans')
    .update({
      status: 'cooked',
      cooked_at: cookedAt.toISOString(),
      cooked_by_member_id: memberId ?? null,
    })
    .eq('id', id);
  if (error) throw error;

  // Decrement pantry per linked ingredient. We run this in the offline worker
  // (no React state), so we fetch the current quantity from the DB and then
  // write a stock_transactions row + update stock_items — mirroring the
  // StockEventHandler.RECORD_USAGE path without re-emitting events.
  const ingredients: MealPlanIngredient[] = plan.ingredients || [];
  for (const ing of ingredients) {
    if (!ing.stockItemId || ing.optional) continue;
    const qty = Math.max(0, ing.neededFromStock > 0 ? ing.neededFromStock : ing.quantity);
    if (!qty) continue;

    const { data: stockRow, error: fetchErr } = await supabase
      .from('stock_items')
      .select('id, current_quantity, name')
      .eq('id', ing.stockItemId)
      .maybeSingle();
    if (fetchErr || !stockRow) {
      console.warn('MealEventHandler: stock item missing for cook decrement', ing.stockItemId);
      continue;
    }

    const current = Number(stockRow.current_quantity ?? 0);
    const newQuantity = Math.max(0, current - qty);

    await supabase.from('stock_transactions').insert({
      stock_item_id: ing.stockItemId,
      team_id: teamId,
      quantity_change: -qty,
      transaction_type: 'use',
      notes: `Meal: ${plan.name}`,
      created_by_member_id: memberId ?? null,
    });
    await supabase.from('stock_items').update({ current_quantity: newQuantity }).eq('id', ing.stockItemId);
    // The Stock realtime channel will pick up the update and refresh the UI.
  }
}

async function handleAttendeeSet(payload: {
  planId: string;
  memberId: string;
  status: 'attending' | 'not_attending' | 'maybe';
  notes?: string | null;
}) {
  const { planId, memberId, status, notes } = payload;
  // Upsert on the unique (meal_plan_id, member_id) constraint so repeated
  // taps just update the row instead of erroring on duplicate keys.
  const { error } = await supabase
    .from('meal_plan_attendees')
    .upsert(
      {
        meal_plan_id: planId,
        member_id: memberId,
        status,
        notes: notes ?? null,
        responded_at: new Date().toISOString(),
      },
      { onConflict: 'meal_plan_id,member_id' },
    );
  if (error) throw error;
}

async function handleAttendeeRemove(payload: { planId: string; memberId: string }) {
  const { error } = await supabase
    .from('meal_plan_attendees')
    .delete()
    .eq('meal_plan_id', payload.planId)
    .eq('member_id', payload.memberId);
  if (error) throw error;
}
